import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const assignmentCompletionApi = {
  /**
   * Get the assignment completion by BU report (ADR-093).
   * One row per terminal BU for the selected period; no pagination.
   * @param {Object} params - Query parameters
   * @param {number} [params.year] - Period year (defaults to current server-side; current year = year-to-date)
   * @param {string} [params.clusterId]
   * @param {boolean} [params.includeSubUnits]
   * @param {string} [params.businessUnitId]
   * @param {string} [params.buScope] - 'direct' | 'hierarchy'
   * @param {string} [params.representativeId] - narrow to the people managed by this
   *   representative (the dashboard card deep-links with self + hierarchy)
   * @param {string} [params.reportingScope] - 'direct' | 'hierarchy' — span of the above
   * @param {string} [params.orgUnitType] - BU organizational unit type CODE; server
   *   defaults an absent param to 'delivery' — send the sentinel 'all' to disable
   * @param {string} [params.assignmentKind] - 'all' | 'session' | 'individual'
   * @param {number} [params.belowRate] - only rows with completionRate < N (lens filter)
   * @param {string} [params.orderBy] - 'completionRate' | 'name'
   * @param {string} [params.orderDirection] - 'asc' | 'desc'
   * @returns {Promise<Object>} { period, summary, rows, dataQuality }
   */
  getAssignmentCompletionReport: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.year) {
        searchParams.set('year', String(params.year));
      }
      if (params.clusterId) {
        searchParams.set('clusterId', params.clusterId);
      }
      if (params.includeSubUnits !== undefined) {
        searchParams.set('includeSubUnits', String(params.includeSubUnits));
      }
      if (params.businessUnitId) {
        searchParams.set('businessUnitId', params.businessUnitId);
      }
      if (params.buScope) {
        searchParams.set('buScope', params.buScope);
      }
      if (params.representativeId) {
        searchParams.set('representativeId', params.representativeId);
        if (params.reportingScope) {
          searchParams.set('reportingScope', params.reportingScope);
        }
      }
      if (params.orgUnitType) {
        searchParams.set('orgUnitType', params.orgUnitType);
      }
      if (params.assignmentKind) {
        searchParams.set('assignmentKind', params.assignmentKind);
      }
      if (params.belowRate !== undefined && params.belowRate !== null && params.belowRate !== '') {
        searchParams.set('belowRate', String(params.belowRate));
      }
      if (params.orderBy) {
        searchParams.set('orderBy', params.orderBy);
      }
      if (params.orderDirection) {
        searchParams.set('orderDirection', params.orderDirection);
      }

      const queryString = searchParams.toString();
      const url = `${API_BASE_URL}/reports/assignment-completion${queryString ? `?${queryString}` : ''}`;

      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch assignment completion report', error.status || 500);
    }
  },

  /**
   * The individuals behind one BU row (ADR-094), with their assignments.
   * Built from the same population as the row, so the list length equals the
   * row's Individuals and the counts sum to its totals.
   * @param {string} businessUnitId
   * @param {Object} [params] - { year, assignmentKind, representativeId, reportingScope }
   * @returns {Promise<Object>} { period, businessUnit, cluster, summary, individuals }
   */
  getServiceUnitIndividuals: async (businessUnitId, params = {}) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.year) {
        searchParams.set('year', String(params.year));
      }
      if (params.assignmentKind && params.assignmentKind !== 'all') {
        searchParams.set('assignmentKind', params.assignmentKind);
      }
      if (params.representativeId) {
        searchParams.set('representativeId', params.representativeId);
        if (params.reportingScope) {
          searchParams.set('reportingScope', params.reportingScope);
        }
      }
      const queryString = searchParams.toString();
      const url = `${API_BASE_URL}/reports/assignment-completion/service-units/${businessUnitId}${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch business unit individuals', error.status || 500);
    }
  },

  /**
   * ADR-095 policies: every Cluster for the year, each with its
   * policy or null (unconfigured clusters come back too, so the config grid is
   * complete and a blank row is something to fill rather than notice).
   * @param {number} year
   * @returns {Promise<{year: number, deliveryCenters: Array}>}
   */
  getPolicies: async (year) => {
    try {
      const search = year ? `?year=${encodeURIComponent(year)}` : '';
      return await authenticatedApi.get(`${API_BASE_URL}/assignment-completion-policies${search}`);
    } catch (error) {
      throw new ApiError('Failed to fetch assignment completion policies', error.status || 500);
    }
  },

  /** Create or update the policy for one (Cluster, year). */
  savePolicy: async ({ clusterId, year, sequentialPerIndividual, parallelPerIndividual }) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/assignment-completion-policies`, {
        clusterId,
        year,
        sequentialPerIndividual,
        parallelPerIndividual,
      });
    } catch (error) {
      throw new ApiError('Failed to save assignment completion policy', error.status || 500);
    }
  },

  /**
   * ADR-096: create or update one Position's override of a Cluster's
   * generic pair. Nested under the policy because an override cannot exist
   * without it — the URL shape says so, matching the schema.
   */
  savePositionPolicy: async ({ policyId, positionId, sequentialPerIndividual, parallelPerIndividual }) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/assignment-completion-policies/${policyId}/positions`,
        { positionId, sequentialPerIndividual, parallelPerIndividual },
      );
    } catch (error) {
      throw new ApiError('Failed to save position override', error.status || 500);
    }
  },

  /**
   * Remove one override — that position falls back to the Cluster's
   * generic pair, which is a different statement from an override of 0 + 0.
   */
  deletePositionPolicy: async ({ policyId, positionId }) => {
    try {
      return await authenticatedApi.delete(
        `${API_BASE_URL}/assignment-completion-policies/${policyId}/positions/${positionId}`,
      );
    } catch (error) {
      throw new ApiError('Failed to remove position override', error.status || 500);
    }
  },

  /**
   * ADR-097: create or update the rule for a whole position TYPE — the middle
   * layer between the Cluster's pair and a single position's override.
   */
  savePositionTypePolicy: async ({ policyId, orgUnitTypeId, sequentialPerIndividual, parallelPerIndividual }) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/assignment-completion-policies/${policyId}/position-types`,
        { orgUnitTypeId, sequentialPerIndividual, parallelPerIndividual },
      );
    } catch (error) {
      throw new ApiError('Failed to save position type rule', error.status || 500);
    }
  },

  /**
   * Remove one type rule — positions of that type fall back to the Delivery
   * Center's generic pair, except any carrying an override of their own.
   */
  deletePositionTypePolicy: async ({ policyId, orgUnitTypeId }) => {
    try {
      return await authenticatedApi.delete(
        `${API_BASE_URL}/assignment-completion-policies/${policyId}/position-types/${orgUnitTypeId}`,
      );
    } catch (error) {
      throw new ApiError('Failed to remove position type rule', error.status || 500);
    }
  },

  /** Remove a policy — the Cluster then expects nothing that year. */
  deletePolicy: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/assignment-completion-policies/${id}`);
    } catch (error) {
      throw new ApiError('Failed to delete assignment completion policy', error.status || 500);
    }
  },

  // ── ADR-108: engagement models excluded from the population, per year ────

  /** The year's exclusions plus the whole taxonomy with impact counts. */
  getExclusions: async (year) => {
    try {
      const search = year ? `?year=${encodeURIComponent(year)}` : '';
      return await authenticatedApi.get(`${API_BASE_URL}/assignment-completion-exclusions${search}`);
    } catch (error) {
      throw new ApiError('Failed to fetch assignment completion exclusions', error.status || 500);
    }
  },

  /** Replace the year's set of excluded engagement models (idempotent). */
  saveExclusions: async ({ year, engagementModelIds }) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/assignment-completion-exclusions`, {
        year,
        engagementModelIds,
      });
    } catch (error) {
      throw new ApiError('Failed to save assignment completion exclusions', error.status || 500);
    }
  },

  /** Add fromYear's exclusions to toYear — never removes anything. */
  copyExclusions: async ({ fromYear, toYear }) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/assignment-completion-exclusions/copy`, {
        fromYear,
        toYear,
      });
    } catch (error) {
      throw new ApiError('Failed to copy assignment completion exclusions', error.status || 500);
    }
  },

  /**
   * Dashboard-card summary (ADR-093, ADR-091 pattern). Org-wide callers
   * (admin / people partner) get period totals + a lagging-first per-cluster
   * breakdown; everyone else gets { supported: false } and the card is not
   * rendered.
   * @param {Object} [params] - { year } (defaults to the current year)
   * @returns {Promise<Object>} { period, …totals, scope, breakdown } | { supported: false }
   */
  getAssignmentCompletionSummary: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.year) {
        searchParams.set('year', String(params.year));
      }
      const queryString = searchParams.toString();
      const url = `${API_BASE_URL}/reports/assignment-completion/summary${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch assignment completion summary', error.status || 500);
    }
  },
};
